import React, { useEffect, useMemo, useState } from 'react';
import { TouchableOpacity } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { BasicLayout, CommonContainer } from '../../../common/components/CustomLayout';
import { BOHTBody, BOHTD, BOHTDIconBox, BOHTH, BOHTHead, BOHTR, BOHTable } from '../../../common/components/bohtable';
import { BOHButton, BOHTlbrSearchInput, BOHToolbar } from '../../../common/components/bohtoolbar';
import { msgStr } from '../../../common/constants/Message';
import { TextMediumSize } from '../../../common/constants/Fonts';
import { useAlertModal, useConfirmModal } from '../../../common/hooks';
import { getReservationsWithManualAddressData, updateReservation, updateReservationManualAddress } from '../../../api/Reservation';
import { useSorting } from '../../../common/hooks/useSorting';
import { Text } from 'react-native';
import AddressModal from './AddressModal';

const AddressAutomation = ({ navigation }) => {
  const { showAlert } = useAlertModal();
  const { showConfirm } = useConfirmModal();

  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [updateAddressesTrigger, setUpdateAddressesTrigger] = useState(true);

  const [selectedReservations, setSelectedReservations] = useState([]);
  const InitialWidths = [40, 140, 300, 300, 300];
  const [searchOptions, setSearchOptions] = useState({
    searchKey: '',
    ShowOnlyManual: true,
  });

  // Modal states
  const [isAddressModalVisible, setIsAddressModalVisible] = useState(false);
  const [currentEditingItem, setCurrentEditingItem] = useState(null);

  const changeSearchOptions = (key, val) => {
    setSearchOptions(prevOptions => ({
      ...prevOptions,
      [key]: val
    }));
  }

  useEffect(() => {
    if (updateAddressesTrigger == true) getTable();
  }, [updateAddressesTrigger]);

  useEffect(() => {
    setUpdateAddressesTrigger(true);
  }, [searchOptions])

  const updateManualAddresses = () => {
    showConfirm('Are you sure you want to update the addresses?', () => {
      setIsLoading(true);
      updateReservationManualAddress({ ids: selectedReservations }, (jsonRes, status, error) => {
        setIsLoading(false);
        switch (status) {
          case 200:
            setUpdateAddressesTrigger(true);
            showAlert('success', jsonRes.message || 'Addresses updated successfully');
            setSelectedReservations([]);
            break;
          default:
            if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
            else showAlert('error', msgStr('unknownError'));
            break;
        }
      });
    });
  };

  const getTable = () => {
    setIsLoading(true);
    getReservationsWithManualAddressData({searchOptions:searchOptions}, (jsonRes, status, error) => {
      switch (status) {
        case 200:
          setUpdateAddressesTrigger(false);
          setTableData(jsonRes);
          break;
        case 500:
          showAlert('error', msgStr('serverError'));
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    });
  };

  const { sortedData, handleSort, sorting } = useSorting('reservations', tableData, { key: 'order_number', direction: 'descending' });

  // Handle checkbox selection for a single item
  const handleSelectReservation = (id) => {
    setSelectedReservations(prevSelected => {
      if (prevSelected.includes(id)) {
        return prevSelected.filter(itemId => itemId !== id);
      } else {
        return [...prevSelected, id];
      }
    });
  };

  // Handle select all checkbox
  const handleSelectAll = () => {
    if (selectedReservations.length === sortedData.length) {
      // If all are selected, unselect all
      setSelectedReservations([]);
    } else {
      // Otherwise select all
      setSelectedReservations(sortedData.map(item => item.id));
    }
  };

  // Function to open the address selection modal
  const openAddressModal = (item) => {
    setCurrentEditingItem(item);
    setIsAddressModalVisible(true);
  };

  // Function to close the address selection modal
  const closeAddressModal = () => {
    setIsAddressModalVisible(false);
    setCurrentEditingItem(null);
  };

  // Handle address selection from modal
  const handleAddressSelected = (address) => {
    if (!address || !currentEditingItem) return;
    
    const payload: any = {
      id: currentEditingItem.id,
      address_id: address.id,
      use_manual: false,
    };

    if(address.id != currentEditingItem.address_id){
      payload.manually_updated = true;
      payload.is_address_synced = false;
    }

     updateReservation(payload, (jsonRes, status) => {
         switch (status) {
           case 201:
             closeAddressModal();
             setUpdateAddressesTrigger(true);
             showAlert('success', 'Address updated successfully');
             break;
           default:
             if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
             else showAlert('error', msgStr('unknownError'));
             break;
         }
       })
  };

  const renderTableData = useMemo(() => {
    const rows = [];
    if (sortedData.length > 0) {
      sortedData.map((item, index) => {
        const isSelected = selectedReservations.includes(item.id);
        rows.push(
          <BOHTR key={index}>
            <BOHTDIconBox width={InitialWidths[0]}>
              <TouchableOpacity
                onPress={() => handleSelectReservation(item.id)}
              >
                <MaterialIcons 
                  size={TextMediumSize} 
                  name={isSelected ? "check-box" : "check-box-outline-blank"} 
                  color={isSelected ? "#007AFF" : "#777"} 
                />
              </TouchableOpacity>
            </BOHTDIconBox>
            <BOHTD width={InitialWidths[1]}>{item.order_number}</BOHTD>
            <BOHTD width={InitialWidths[2]}>
              <TouchableOpacity onPress={() => openAddressModal(item)}>
                <Text style={{ color: item.address_id ? 'black' : '#007AFF' }}>
                  {item.address || 'Select address'}
                </Text>
              </TouchableOpacity>
            </BOHTD>
            <BOHTD width={InitialWidths[3]}>{item.temp_address}</BOHTD>
            <BOHTD width={InitialWidths[4]}>{item.manual_address}</BOHTD>
          </BOHTR>
        );
      });
    } else {
      <></>;
    }

    setIsLoading(false);
    return <>{rows}</>;
  }, [sortedData, selectedReservations]);

  const headers = [
    { key: 'order_number', label: 'Order Number' },
    { key: 'address', label: 'Address' },
    { key: 'temp_address', label: 'Temp Address' },
    { key: 'manual_address', label: 'Manual Address' },
  ];

  const tableElement = useMemo(() => (
    <BOHTable isLoading={isLoading}>
      <BOHTHead>
        <BOHTR>
          <BOHTDIconBox width={InitialWidths[0]}>
            <TouchableOpacity onPress={handleSelectAll}>
              <MaterialIcons 
                size={TextMediumSize} 
                name={selectedReservations.length === sortedData.length && sortedData.length > 0 ? "check-box" : "check-box-outline-blank"} 
                color={selectedReservations.length > 0 ? "#007AFF" : "#777"} 
              />
            </TouchableOpacity>
          </BOHTDIconBox>
          {headers.map(({ key, label }, wIndex) => (
            <BOHTH
              key={key}
              width={InitialWidths[wIndex + 1]} // +1 to account for checkbox column
              isSorted={sorting.key === key}
              direction={sorting.key === key ? sorting.direction : null}
              onClick={() => handleSort(key)}
            >
              {label}
            </BOHTH>
          ))}
        </BOHTR>
      </BOHTHead>
      <BOHTBody>
        {renderTableData}
      </BOHTBody>
    </BOHTable>), [isLoading, sortedData, selectedReservations])

  return (
    <BasicLayout
      navigation={navigation}
      goBack={() => {
        navigation.navigate('Marketing');
      }}
      screenName={'Address Automation'}
    >
      <CommonContainer>
        <BOHToolbar>
          {selectedReservations.length > 0 && (
            <BOHButton
              style={{ marginLeft: 8 }}
              label={`Update Addresses (${selectedReservations.length})`}
              onPress={updateManualAddresses}
            />
          )}
          <BOHTlbrSearchInput
            label="Search"
            value={searchOptions.searchKey}
            onChangeText={(val) => changeSearchOptions('searchKey', val)} />
        </BOHToolbar>
        {tableElement}
      </CommonContainer>
      
      {/* Address Selection Modal */}
      <AddressModal
        isModalVisible={isAddressModalVisible}
        closeModal={closeAddressModal}
        initialValue={currentEditingItem}
        onSave={handleAddressSelected}
      />

    </BasicLayout>
  );
};

export default AddressAutomation;
